import { styled } from "@linaria/react"
import { ReactQueryDevtools } from "@tanstack/react-query-devtools"
import { Outlet } from "@tanstack/react-router"
import { TanStackRouterDevtools } from "@tanstack/react-router-devtools"
import { Fragment, useEffect, useState } from "react"

import { Centered } from "@/components/Centered/Centered.ts"
import { Horizontal } from "@/components/Flex.ts"
import { ScrollToTop } from "@/components/ScrollToTop/ScrollToTop.tsx"
import { useDevMode } from "@/hooks/useDevMode.ts"
import m1 from "@/icons/m-1.svg?react"
import m2 from "@/icons/m-2.svg?react"
import m3 from "@/icons/m-3.svg?react"


export const RootScene = () => {
  const devMode = useDevMode()

  return (
    <Fragment>
      <Outlet />
      <ScrollToTop />
      {window.parent === window && devMode && <TanStackRouterDevtools position="bottom-right" />}
      {window.parent === window && devMode && <ReactQueryDevtools initialIsOpen={false} />}
    </Fragment>
  )
}

const height = 18

const m1Width = 27
const m2Width = 21
const m3Width = 37

const maxMiddleCount = 15
const offset = 10

export const Assembling = () => {
  const [middleCount, setMiddleCount] = useState(1)
  const frameDelay = 70 // ms between frames
  const pauseDelay = 700 // ms to pause at max expansion

  useEffect(() => {
    let animationFrame = 1
    let timeoutId: number | undefined

    const runAnimation = () => {
      // Set the current frame
      setMiddleCount(animationFrame)

      // Schedule the next frame
      timeoutId = window.setTimeout(() => {
        if (animationFrame < maxMiddleCount) {
          // Still expanding
          animationFrame++
          runAnimation()
        } else {
          // At max expansion, pause before resetting
          timeoutId = window.setTimeout(() => {
            // Reset to frame 1
            animationFrame = 1
            setMiddleCount(animationFrame)

            // Start the next cycle after a frame delay
            timeoutId = window.setTimeout(() => {
              animationFrame++
              runAnimation()
            }, frameDelay)
          }, pauseDelay)
        }
      }, frameDelay)
    }

    // Start with frame 1
    runAnimation()

    // Cleanup
    return () => {
      if (timeoutId) {
        window.clearTimeout(timeoutId)
      }
    }
  }, [])

  return (
    <CoverCentered>
      <Container>
        <M1 />

        {/* Render multiple m-2.svg elements based on the current count */}
        {Array.from({ length: middleCount }).map((_, index) => (
          <M2 key={index} />
        ))}

        <M3 />
      </Container>
    </CoverCentered>
  )
}

const CoverCentered = styled(Centered)`
  position: absolute;
  z-index: 1;
  inset: 0;
`

const Container = styled(Horizontal)`
  width: ${(m1Width + (m2Width * maxMiddleCount) + m3Width) - (offset * (maxMiddleCount + 1))}px;
  height: ${height}px;
`

const M1 = styled(m1)`
  width: ${m1Width}px;
`

const M2 = styled(m2)`
  width: ${m2Width}px;
  margin-left: -${offset}px;
`

const M3 = styled(m3)`
  width: ${m3Width}px;
  margin-left: -${offset}px;
`
